import { useState } from "react"
import { isNodeSelection } from "@tiptap/core"
import { BubbleMenu } from "@tiptap/react"
import {
	IconPaperclipOutline18 as Paperclip,
	IconTrash2Outline18 as Trash2,
} from "nucleo-ui-outline-18"

import { Tooltip } from "@productlane/ui"

import type { TabType } from "./types"
import type { Editor } from "@tiptap/react"
import { liveSelectionRect } from "@/components/editor/live-selection-rect"
import { EditorMenuButton } from "@/components/editor/menu/editor-menu-button"
import { toast } from "@/components/toast"
import { BubbleMenuFormatButtons } from "./bubble-menu-format-buttons"

interface AddExistingAttachmentParams {
	url: string
	name: string
	type: string
}

interface ComposeEditorBubbleMenusProps {
	composeEditor: Editor
	activeTab: TabType
	addExistingAttachment: (params: AddExistingAttachmentParams) => void
}

function getMimeTypeFromUrl(url: string): string {
	const ext = url.split("?")[0]?.split(".").pop()?.toLowerCase()
	const map: Record<string, string> = {
		jpg: "image/jpeg",
		jpeg: "image/jpeg",
		png: "image/png",
		gif: "image/gif",
		webp: "image/webp",
		svg: "image/svg+xml",
	}
	return map[ext ?? ""] ?? "image/png"
}

export function ComposeEditorBubbleMenus({
	composeEditor,
	activeTab,
	addExistingAttachment,
}: ComposeEditorBubbleMenusProps) {
	const [isAddingLink, setIsAddingLink] = useState(false)
	const [linkUrl, setLinkUrl] = useState("")

	return (
		<>
			<BubbleMenu
				editor={composeEditor}
				tippyOptions={{
					getReferenceClientRect: liveSelectionRect(composeEditor),
					duration: 100,
					placement: "top",
					zIndex: 40,
					popperOptions: {
						modifiers: [
							{
								name: "customStyle",
								enabled: true,
								phase: "beforeWrite",
								fn({ state }) {
									const tippyBox = state.elements.popper
										.firstChild as HTMLElement | null
									if (tippyBox) {
										tippyBox.style.maxWidth = "none"
									}
								},
							},
						],
					},
				}}
				shouldShow={({ editor }) => {
					const { selection } = editor.state
					const isImageNode =
						isNodeSelection(selection) && selection.node.type.name === "image"
					return (
						activeTab === "reply" &&
						!!selection &&
						!selection.empty &&
						!isImageNode
					)
				}}
			>
				<div className="flex whitespace-nowrap rounded-lg border border-gray-200 bg-white p-1 font-medium text-gray-500 shadow-sm ring-1 ring-gray-300/30 dark:border-gray-800 dark:bg-gray-850 dark:text-gray-200 dark:ring-gray-700/40">
					{isAddingLink ? (
						<div className="flex items-center gap-1">
							<input
								type="url"
								value={linkUrl}
								onChange={(e) => setLinkUrl(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter" && linkUrl) {
										composeEditor
											.chain()
											.focus()
											.extendMarkRange("link")
											.setLink({
												href: linkUrl,
												target: "_blank",
											})
											.run()
										setLinkUrl("")
										setIsAddingLink(false)
									}
									if (e.key === "Escape") {
										setLinkUrl("")
										setIsAddingLink(false)
									}
								}}
								placeholder="https://..."
								className="w-48 rounded border border-gray-200 bg-transparent px-2 py-1 text-sm outline-none dark:border-gray-700"
								autoFocus
							/>
						</div>
					) : (
						<BubbleMenuFormatButtons
							editor={composeEditor}
							onAddLink={() => setIsAddingLink(true)}
						/>
					)}
				</div>
			</BubbleMenu>

			<BubbleMenu
				editor={composeEditor}
				tippyOptions={{
					getReferenceClientRect: liveSelectionRect(composeEditor),
					duration: 100,
					placement: "top",
					zIndex: 40,
				}}
				shouldShow={({ editor }) => {
					const { selection } = editor.state
					return (
						activeTab === "reply" &&
						isNodeSelection(selection) &&
						selection.node.type.name === "image"
					)
				}}
			>
				<div className="flex whitespace-nowrap rounded-lg border border-gray-200 bg-white p-1 font-medium text-gray-500 shadow-sm ring-1 ring-gray-300/30 dark:border-gray-800 dark:bg-gray-850 dark:text-gray-200 dark:ring-gray-700/40">
					<Tooltip shouldWrapChildren label="Send as attachment">
						<EditorMenuButton
							onClick={() => {
								const { selection } = composeEditor.state
								if (!isNodeSelection(selection)) return
								const src = selection.node.attrs.src as string
								if (!src) return
								if (src.startsWith("data:")) {
									toast.info({
										title: "Image still uploading",
										description:
											"Please wait until the image has finished uploading.",
									})
									return
								}
								const name = src.split("?")[0]?.split("/").pop() ?? "image"
								const type = getMimeTypeFromUrl(src)
								addExistingAttachment({ url: src, name, type })
								composeEditor.chain().focus().deleteSelection().run()
							}}
							isActive={false}
						>
							<Paperclip className="h-4 w-4" />
						</EditorMenuButton>
					</Tooltip>
					<Tooltip shouldWrapChildren label="Delete image">
						<EditorMenuButton
							onClick={() => {
								composeEditor.chain().focus().deleteSelection().run()
							}}
							isActive={false}
						>
							<Trash2 className="h-4 w-4" />
						</EditorMenuButton>
					</Tooltip>
				</div>
			</BubbleMenu>
		</>
	)
}
